/**
 * Format date to 'MMM dd, yyyy • hh:mm a' format
 * Example: 'Jan 15, 2024 • 02:30 PM'
 */
const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12
  const hoursStr = String(hours).padStart(2, "0");

  return `${month} ${day}, ${year} • ${hoursStr}:${minutes} ${ampm}`;
};

// Alternative: ISO format for inputs
const formatDateISO = (date) => {
  return new Date(date).toISOString();
};

/**
 * Format work hours for display
 * @param {string[]} hours - Array of work hour periods
 * @returns {string[]|null} - Formatted hour labels or null
 */
const formatWorkHours = (hours) => {
  if (!hours || !Array.isArray(hours) || hours.length === 0) return null;

  const timeLabels = {
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
    "whole day": "Full Day",
  };

  return hours.map((h) => timeLabels[h] || h);
};

/**
 * Format days array to shortened 3-letter format
 * @param {string[]} days - Array of day names
 * @returns {string[]|null} - Shortened day names or null
 */
const formatDays = (days) => {
  if (!days || !Array.isArray(days) || days.length === 0) return null;
  // Shorten day names for display
  return days.map((day) => day.substring(0, 3));
};

/**
 * Format experience level display
 * @param {string} experienceLevel - Experience level code
 * @returns {string} - Human readable experience level
 */
const formatExperienceDisplay = (experienceLevel) => {
  if (!experienceLevel) return "Any";

  // Return the raw experience level range (e.g., "1-5", "6-10", "11-15", "15-20")
  return experienceLevel;
};

/**
 * Format location string from subcity and city
 * @param {string} subcity - Subcity/area name
 * @param {string} location - City name
 * @returns {string} - Formatted location string
 */
const formatLocation = (subcity, location) => {
  const area = subcity || "Unknown";
  const city = location || "Addis Ababa";

  if (area === "Unknown") {
    return city;
  }
  return `${area} subcity, ${city}`;
};

/**
 * Get category display label
 * @param {string} category - Category code
 * @returns {string} - Human readable category name
 */
const getCategoryLabel = (category) => {
  const labels = {
    childcare: "Child Care",
    cook: "Cooking",
    cleaner: "House Cleaning",
    homeTutor: "Home Tutoring",
    elderlycare: "Elderly Care",
  };
  return labels[category] || "General Care";
};

/**
 * Format work type display
 * @param {string} workTime - Work time code
 * @returns {string} - Human readable work type
 */
const formatWorkType = (workTime) => {
  const workTypes = {
    "part-time": "Part Time",
    "full-time": "Full Time",
    "living-in": "Live In",
    "night-shift": "Night Shift",
    "on-demand": "On Demand",
  };
  return workTypes[workTime] || "Full Time";
};

/**
 * Format salary display
 * @param {number|string} salary - Salary amount
 * @returns {string} - Formatted salary string
 */
const formatSalary = (salary) => {
  return salary ? `${salary} ETB` : "Negotiable";
};

/**
 * Format short date display
 * @param {string} dateString - ISO date string
 * @returns {string} - Short date format (e.g., "Jan 15")
 */
const formatShortDate = (dateString) => {
  if (!dateString) return "Recently";

  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

export {
  formatDate,
  formatDateISO,
  formatWorkHours,
  formatDays,
  formatExperienceDisplay,
  formatLocation,
  getCategoryLabel,
  formatWorkType,
  formatSalary,
  formatShortDate,
};

export default {
  formatDate,
  formatDateISO,
  formatWorkHours,
  formatDays,
  formatExperienceDisplay,
  formatLocation,
  getCategoryLabel,
  formatWorkType,
  formatSalary,
  formatShortDate,
};
